import React, { useEffect } from 'react';

// Import hooks
import { useNotifications } from './hooks/useNotifications';
import { useCustomerState } from './hooks/useCustomerState';
import { useCustomerData } from './hooks/useCustomerData';
import { useQuickBooksIntegration } from './hooks/useQuickBooksIntegration';
import useTechnicianFilter from '../../hooks/useTechnicianFilter';

// Import services
import { customerActions } from './services/customerActions';

// Import components
import CustomerHeader from './components/CustomerHeader';
import CustomerSearch from './components/CustomerSearch';
import CustomerTable from './components/CustomerTable';
import CustomerPagination from './components/CustomerPagination';
import NewRecordsBanner from './components/NewRecordsBanner';

const CustomerList = ({ onCustomerSelect, onAddCustomer, onEditCustomer, currentUser, userProfile, onAddJob, onAddContract }) => {
  // Initialize hooks
  const notifications = useNotifications();
  const customerState = useCustomerState();
  const techFilter = useTechnicianFilter(userProfile);
  const customerData = useCustomerData(
    userProfile,
    customerState.debouncedSearchTerm,
    customerState.currentPage,
    customerState.setCustomers,
    customerState.setCustomerCount,
    customerState.setPagination,
    customerState.setIsLoading,
    notifications.showNotification,
    customerState.sortBy,
    techFilter
  );

  // Handle new customers for real-time updates
  const handleNewCustomers = (newCustomers, progressData) => {
    customerActions.handleNewCustomers(
      newCustomers,
      progressData,
      customerState.currentPage,
      customerState.debouncedSearchTerm,
      customerState.setCustomers,
      customerState.setCustomerCount,
      customerState.setPagination,
      customerState.setHasNewRecords
    );
  };

  const quickbooks = useQuickBooksIntegration(
    userProfile,
    currentUser,
    notifications.showNotification,
    handleNewCustomers
  );

  // Initialize QuickBooks connection check and search reset
  useEffect(() => {
    quickbooks.checkQuickBooksConnection();
    customerState.resetToFirstPage(); // Reset to first page when searching
  }, [customerState.debouncedSearchTerm]);

  // Handle SSE import completion
  useEffect(() => {
    if (quickbooks.isSSECompleted) {
      const isCompleted = quickbooks.handleImportComplete();
      if (isCompleted) {
        if (customerState.currentPage !== 1 || customerState.debouncedSearchTerm) {
          // If not on page 1 or searching, show notification to refresh
          customerState.setHasNewRecords(true);
          notifications.showNotification('Import completed! Refresh to see all new customers.', 'success');
        } else {
          // Always refresh the customer list after import to ensure all data is current
          customerData.loadCustomers(1, customerState.debouncedSearchTerm);
          notifications.showNotification('Import completed! Customer list updated.', 'success');
        }
      }
    }
  }, [quickbooks.isSSECompleted]);

  // Clear new records when page changes or search changes
  useEffect(() => {
    if (quickbooks.newRecords.length > 0) {
      quickbooks.clearNewRecords();
    }
    customerState.setHasNewRecords(false);
  }, [customerState.currentPage, customerState.debouncedSearchTerm]);

  // Event handlers
  const handleDeleteCustomer = (customer) => {
    customerActions.handleDeleteCustomer(
      customer,
      customerData.getFranchiseeId,
      customerState.setCustomers,
      customerState.setCustomerCount,
      customerState.setPagination,
      notifications.showNotification
    );
  };

  const handleDeleteAllCustomers = () => {
    customerActions.handleDeleteAllCustomers(
      userProfile,
      customerState.customerCount,
      customerState.customers,
      customerData.getFranchiseeId,
      customerState.setCustomers,
      customerState.setCustomerCount,
      customerState.setPagination,
      customerState.setCurrentPage,
      notifications.showNotification
    );
  };

  const handleGoToFirstPage = () => {
    customerState.setCurrentPage(1);
    customerState.setHasNewRecords(false);
  };

  return (
    <div className="px-2 py-2 md:p-6">
      <CustomerHeader
        isQbConnected={quickbooks.isQbConnected}
        isImporting={quickbooks.isImporting}
        isSSEImporting={quickbooks.isSSEImporting}
        sseProgress={quickbooks.sseProgress}
        importProgress={quickbooks.importProgress}
        customers={customerState.customers}
        customerCount={customerState.customerCount}
        userProfile={userProfile}
        onAddCustomer={onAddCustomer}
        onAddJob={onAddJob}
        onAddContract={onAddContract}
        onImportFromQuickBooks={quickbooks.handleImportFromQuickBooks}
        onDeleteAllCustomers={handleDeleteAllCustomers}
        techFilter={techFilter}
      />

      <div className="bg-white rounded-lg shadow-md">
        <NewRecordsBanner
          hasNewRecords={customerState.hasNewRecords}
          isOnFirstPage={customerState.isOnFirstPage}
          onGoToFirstPage={handleGoToFirstPage}
          onDismiss={() => customerState.setHasNewRecords(false)}
        />

        <CustomerSearch
          searchTerm={customerState.searchTerm}
          onSearchChange={customerState.setSearchTerm}
          sortBy={customerState.sortBy}
          onSortChange={customerState.setSortBy}
        />

        <CustomerTable
          customers={customerState.customers}
          isLoading={customerState.isLoading}
          searchTerm={customerState.searchTerm}
          onCustomerSelect={onCustomerSelect}
          onDeleteCustomer={handleDeleteCustomer}
          onEditCustomer={onEditCustomer}
          userProfile={userProfile}
        />

        <CustomerPagination
          pagination={customerState.pagination}
          currentPage={customerState.currentPage}
          searchTerm={customerState.searchTerm}
          isLoading={customerState.isLoading}
          onPageChange={customerState.handlePageChange}
        />
      </div>

    </div>
  );
};

export default CustomerList;